import type { ChipProps } from '@mui/material'
import type {
  SocialItemStatus,
  SocialItemTarget,
  SocialPlatform,
  SocialPublication,
  SocialPublicationItem,
  SocialPublicationStatus,
} from '@/api/social'

export const ITEM_STATUS_COLOR: Record<SocialItemStatus, ChipProps['color']> = {
  pending: 'default',
  sending: 'default',
  sent: 'success',
  failed: 'error',
  needs_human: 'warning',
  needs_credentials: 'warning',
  needs_captcha: 'warning',
}

export const PUBLICATION_STATUS_COLOR: Record<SocialPublicationStatus, ChipProps['color']> = {
  awaiting_approval: 'warning',
  approved: 'info',
  finished: 'success',
  cancelled: 'default',
}

/** Имя цели для человека: название из кеша проверки, иначе ключ */
export function targetLabel(
  target: Pick<SocialItemTarget, 'key' | 'title'> | null | undefined,
  fallbackId: number,
): string {
  return target?.title || target?.key || `#${fallbackId}`
}

/** Повтор, который примет API: needs_* или failed с retryable, публикация не отменена */
export function canRetryItem(item: SocialPublicationItem, publication: SocialPublication): boolean {
  if (publication.status === 'cancelled') {
    return false
  }

  return (
    item.status === 'needs_human' ||
    item.status === 'needs_credentials' ||
    item.status === 'needs_captcha' ||
    (item.status === 'failed' && item.retryable)
  )
}

const MARKABLE_AS_SENT: ReadonlySet<SocialItemStatus> = new Set([
  'pending',
  'failed',
  'needs_human',
  'needs_credentials',
  'needs_captcha',
])

/** «Опубликовано вручную» — из любого статуса, кроме sending и sent */
export function canMarkItemSent(item: SocialPublicationItem): boolean {
  return MARKABLE_AS_SENT.has(item.status)
}

export function canCancelPublication(publication: SocialPublication): boolean {
  return publication.status === 'awaiting_approval' || publication.status === 'approved'
}

/**
 * Уже созданные отложенные записи VK: шаг VK отправлен, а publish_at ещё
 * впереди. После отмены публикации они остаются в VK — удалять вручную.
 */
export function scheduledVkPosts(publication: SocialPublication, now: number = Date.now()): SocialPublicationItem[] {
  if (!publication.publish_at || new Date(publication.publish_at).getTime() <= now) {
    return []
  }

  return publication.items.filter((item) => item.status === 'sent' && item.target?.platform === 'vk')
}

/** Платформы шагов публикации в порядке Telegram → VK, без повторов */
export function publicationPlatforms(publication: SocialPublication): SocialPlatform[] {
  const present = new Set(publication.items.map((item) => item.target?.platform))

  return (['telegram', 'vk'] as const).filter((platform) => present.has(platform))
}
